"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";

export function LoginForm() {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);

    const data = new FormData(event.currentTarget);

    try {
      const response = await fetch("/api/staff/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: String(data.get("username") ?? ""),
          password: String(data.get("password") ?? ""),
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        setError(body?.error ?? "Could not sign in.");
        setPending(false);
        return;
      }

      // The session cookie is set; `refresh()` makes the server re-render with it before we
      // navigate, so the scan screen never flashes its signed-out state.
      router.replace("/");
      router.refresh();
    } catch {
      setError("Could not reach the XFit service. Check the connection.");
      setPending(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="username" className="block text-sm font-medium">
          Username
        </label>
        <input
          id="username"
          name="username"
          type="text"
          required
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          // Staff usernames are typed on a phone keyboard; autocapitalise turns "amal" into
          // "Amal" and the login 401s with no visible cause.
          className="h-12 w-full rounded-xl border border-border bg-card px-4 text-base outline-none focus:border-brand"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="password" className="block text-sm font-medium">
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          required
          autoComplete="current-password"
          className="h-12 w-full rounded-xl border border-border bg-card px-4 text-base outline-none focus:border-brand"
        />
      </div>

      {error ? (
        <p role="alert" className="rounded-xl bg-deny/15 px-4 py-3 text-sm text-deny">
          {error}
        </p>
      ) : null}

      <Button type="submit" size="lg" disabled={pending}>
        {pending ? <Loader2 className="size-5 animate-spin" aria-hidden /> : null}
        {pending ? "Signing in…" : "Sign in"}
      </Button>

      <Link
        href="/forgot-password"
        className="block text-center text-sm font-medium text-brand underline underline-offset-4"
      >
        Forgot your password?
      </Link>
    </form>
  );
}
